/**
 * Consultation Audit Logger
 * 
 * Standardized logging utility for video consultation lifecycle events.
 * All logs are written to the audit_report table with consistent format:
 * {message} [PUCODE:{pucode}] [ACTOR:{actor}] [EVENT:{event}]
 * 
 * @module consultationAuditLogger
 */

import { writeAuditLog } from '../components/common/apis';

/**
 * Consultation Event Types
 */
export const CONSULTATION_EVENTS = {
  // Kiosk Events
  CONSULT_INITIATED: 'CONSULT_INITIATED',
  CONSULT_ENDED_BY_PATIENT: 'CONSULT_ENDED_BY_PATIENT',
  CONSULT_ENDED_AFTER_ACCEPTANCE: 'CONSULT_ENDED_AFTER_ACCEPTANCE',
  CONSULT_DISCONNECTED: 'CONSULT_DISCONNECTED',
  
  // Pharmacist Events
  CONSULT_REQUEST_RECEIVED: 'CONSULT_REQUEST_RECEIVED',
  CONSULT_ACCEPTED: 'CONSULT_ACCEPTED',
  CONSULT_DECLINED: 'CONSULT_DECLINED',
  CONSULT_STARTED: 'CONSULT_STARTED',
  CONSULT_ENDED_BY_PHARMACIST: 'CONSULT_ENDED_BY_PHARMACIST',
  CONSULT_PUT_ON_HOLD: 'CONSULT_PUT_ON_HOLD',
  CONSULT_RESUMED: 'CONSULT_RESUMED',
  
  // System Events
  CONSULT_NOTIFICATION_SENT: 'CONSULT_NOTIFICATION_SENT',
  CONSULT_NOTIFICATION_DELIVERED: 'CONSULT_NOTIFICATION_DELIVERED',
  CONSULT_NOTIFICATION_FAILED: 'CONSULT_NOTIFICATION_FAILED',
  CONSULT_ACCEPTED_BY_ANOTHER: 'CONSULT_ACCEPTED_BY_ANOTHER',
  CONSULT_TIMEOUT: 'CONSULT_TIMEOUT',
  CONSULT_COMPLETED: 'CONSULT_COMPLETED',
  CONSULT_TERMINATED: 'CONSULT_TERMINATED',
  
  // Error Events
  CONSULT_ACCEPT_FAILED: 'CONSULT_ACCEPT_FAILED',
  CONSULT_HOLD_FAILED: 'CONSULT_HOLD_FAILED',
  CONSULT_RESUME_FAILED: 'CONSULT_RESUME_FAILED',
  CONSULT_END_FAILED: 'CONSULT_END_FAILED',
  CONSULT_DECLINE_FAILED: 'CONSULT_DECLINE_FAILED',
  CONSULT_SILENCE_FAILED: 'CONSULT_SILENCE_FAILED',
  
  // WebSocket Status Events
  MAIN_SOCKET_DEGRADED_ON_CALL: 'MAIN_SOCKET_DEGRADED_ON_CALL',
  PRESENCE_SOCKET_DEGRADED_ON_CALL: 'PRESENCE_SOCKET_DEGRADED_ON_CALL',
  
  // WebSocket Initial Connection Events
  MAIN_SOCKET_INITIAL_CONNECTION_FAILED: 'MAIN_SOCKET_INITIAL_CONNECTION_FAILED',
  MAIN_SOCKET_CONNECTION_RECOVERED: 'MAIN_SOCKET_CONNECTION_RECOVERED',
  PRESENCE_SOCKET_INITIAL_CONNECTION_FAILED: 'PRESENCE_SOCKET_INITIAL_CONNECTION_FAILED',
  PRESENCE_SOCKET_CONNECTION_RECOVERED: 'PRESENCE_SOCKET_CONNECTION_RECOVERED',
};

/**
 * Actors
 */
export const ACTORS = {
  KIOSK: 'KIOSK',
  PHARMACIST: 'PHARMACIST',
  SYSTEM: 'SYSTEM',
};

/**
 * Build description with PUCODE for searchability
 * PUCODE is included in description for search but will be removed from table display
 * Other metadata (ACTOR, EVENT) is stored in auditContext
 * 
 * @param {string} message - Human-readable message
 * @param {string} pucode - Pickup code for searchability
 * @returns {string} Description with PUCODE marker
 */
const buildDescription = (message, pucode) => {
  // Add PUCODE to description for searchability in Audit Trail search field
  // This will be removed from table display by getCleanDescription but remains searchable
  if (pucode) {
    return `${message} [PUCODE:${pucode.toUpperCase()}]`;
  }
  return message;
};

/**
 * Log a consultation lifecycle event
 * 
 * @param {Object} params - Logging parameters
 * @param {string} params.pucode - Pickup code (required)
 * @param {string} params.actor - Actor (KIOSK, PHARMACIST, SYSTEM)
 * @param {string} params.event - Event identifier from CONSULTATION_EVENTS
 * @param {string} params.message - Human-readable message
 * @param {Object} params.metadata - Optional metadata (prescriptionMasterId, etc.)
 * @returns {Promise<void>}
 */
export const logConsultationEvent = async ({
  pucode,
  actor,
  event,
  message,
  metadata = {},
}) => {
  // Validate required parameters
  if (!pucode) {
    console.error('[ConsultationAuditLogger] Missing required parameter: pucode');
    return;
  }
  
  if (!actor) {
    console.error('[ConsultationAuditLogger] Missing required parameter: actor');
    return;
  }
  
  if (!event) {
    console.error('[ConsultationAuditLogger] Missing required parameter: event');
    return;
  }
  
  if (!message) {
    console.error('[ConsultationAuditLogger] Missing required parameter: message');
    return;
  }
  
  try {
    // Build description with PUCODE for searchability
    // PUCODE will be removed from table display but remains searchable
    const description = buildDescription(message, pucode);
    
    // Store metadata in auditContext for User Details modal
    // EVENT and ACTOR are added to description by decorateAuditDescription
    // PUCODE is in description for searchability
    const auditContext = {
      eventType: event,
      source: actor === ACTORS.KIOSK ? 'KIOSK' : actor === ACTORS.PHARMACIST ? 'PORTAL' : 'SYSTEM',
      severity: 'INFO',
      metadata: {
        pucode: pucode ? pucode.toUpperCase() : null,
        actor: actor,
        event: event,
        ...metadata
      }
    };
    
    // Write to audit_report table
    // writeAuditLog signature: (description, action, obj, auditContext)
    // decorateAuditDescription will add [EVENT:xxx] and [ACTOR:xxx] to description
    // getCleanDescription will remove all markers from table display
    await writeAuditLog(
      description,
      'Consultation',
      metadata || {},
      auditContext
    );
    
    // Log to console for debugging (can be removed in production)
    console.log(`[ConsultationAuditLogger] ${description}`, { pucode, actor, event });
  } catch (error) {
    // Silently fail - logging should never break the consultation flow
    console.error('[ConsultationAuditLogger] Failed to write audit log', {
      error: error.message,
      pucode,
      actor,
      event,
    });
  }
};

/**
 * Convenience functions for common events
 */

export const logConsultInitiated = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.KIOSK,
    event: CONSULTATION_EVENTS.CONSULT_INITIATED,
    message: 'Consultation request initiated from kiosk',
    metadata,
  });
};

export const logConsultRequestReceived = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_REQUEST_RECEIVED,
    message: 'Pharmacist received consultation request',
    metadata,
  });
};

export const logConsultAccepted = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_ACCEPTED,
    message: 'Pharmacist accepted consultation request',
    metadata,
  });
};

export const logConsultDeclined = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_DECLINED,
    message: 'Pharmacist declined consultation request',
    metadata,
  });
};

export const logConsultStarted = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_STARTED,
    message: 'Pharmacist started video consultation',
    metadata,
  });
};

export const logConsultEndedByPharmacist = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_ENDED_BY_PHARMACIST,
    message: 'Pharmacist ended consultation session',
    metadata,
  });
};

export const logConsultEndedByPatient = async (pucode, wasAccepted = false, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.KIOSK,
    event: wasAccepted 
      ? CONSULTATION_EVENTS.CONSULT_ENDED_AFTER_ACCEPTANCE
      : CONSULTATION_EVENTS.CONSULT_ENDED_BY_PATIENT,
    message: wasAccepted
      ? 'Patient ended consultation call after acceptance'
      : 'Patient ended consultation call',
    metadata,
  });
};

export const logConsultTimeout = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.CONSULT_TIMEOUT,
    message: 'Consultation request timed out with no response',
    metadata,
  });
};

export const logConsultCompleted = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.CONSULT_COMPLETED,
    message: 'Consultation session completed successfully',
    metadata,
  });
};

export const logConsultPutOnHold = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_PUT_ON_HOLD,
    message: 'Pharmacist put consultation on hold',
    metadata,
  });
};

export const logConsultResumed = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_RESUMED,
    message: 'Pharmacist resumed consultation from hold',
    metadata,
  });
};

export const logConsultAcceptedByAnother = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.CONSULT_ACCEPTED_BY_ANOTHER,
    message: 'Consultation request accepted by another pharmacist',
    metadata,
  });
};

export const logConsultNotificationSent = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.CONSULT_NOTIFICATION_SENT,
    message: 'Consultation request notification sent to pharmacist',
    metadata,
  });
};

export const logConsultNotificationDelivered = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.CONSULT_NOTIFICATION_DELIVERED,
    message: 'Consultation request notification delivered to pharmacist portal',
    metadata,
  });
};

export const logConsultNotificationFailed = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.CONSULT_NOTIFICATION_FAILED,
    message: 'Consultation request notification failed to deliver',
    metadata,
  });
};

export const logConsultDisconnected = async (pucode, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.KIOSK,
    event: CONSULTATION_EVENTS.CONSULT_DISCONNECTED,
    message: 'Patient disconnected from consultation',
    metadata,
  });
};

export const logConsultTerminated = async (pucode, reason = '', metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.CONSULT_TERMINATED,
    message: `Consultation terminated due to system or connectivity issue${reason ? `: ${reason}` : ''}`,
    metadata,
  });
};

/**
 * Notification UI specific logging functions
 * These log actions taken from the Incoming Call Notification UI
 */

export const logIncomingCallReceived = async (pucode, patientName, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_REQUEST_RECEIVED,
    message: `User got the incoming call of ${patientName || 'patient'}`,
    metadata: { ...metadata, patientName },
  });
};

export const logIncomingCallDeclined = async (pucode, patientName, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_DECLINED,
    message: `User Declined the incoming call of ${patientName || 'patient'}`,
    metadata: { ...metadata, patientName },
  });
};

export const logIncomingCallSilenced = async (pucode, patientName, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_PUT_ON_HOLD, // Using hold event as it's similar
    message: `User Silenced the incoming call of ${patientName || 'patient'}`,
    metadata: { ...metadata, patientName },
  });
};

export const logIncomingCallEndAndAccepted = async (pucode, patientName, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_ACCEPTED,
    message: `User End & Accepted the incoming call of ${patientName || 'patient'}`,
    metadata: { ...metadata, patientName },
  });
};

export const logIncomingCallHoldAndAccepted = async (pucode, patientName, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_ACCEPTED,
    message: `User Hold & Accepted the incoming call of ${patientName || 'patient'}`,
    metadata: { ...metadata, patientName },
  });
};

export const logCallPutOnHold = async (pucode, patientName, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_PUT_ON_HOLD,
    message: `User kept ${patientName || 'patient'} on Hold`,
    metadata: { ...metadata, patientName },
  });
};

export const logHeldCallResumed = async (pucode, patientName, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_RESUMED,
    message: `User resumed the Hold call of ${patientName || 'patient'}`,
    metadata: { ...metadata, patientName },
  });
};

export const logHeldCallDeclined = async (pucode, patientName, metadata = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_ENDED_BY_PHARMACIST,
    message: `User declined the Hold call of ${patientName || 'patient'}`,
    metadata: { ...metadata, patientName },
  });
};

/**
 * Error logging functions
 * These log errors that occur during consultation operations
 */

export const logConsultAcceptFailed = async (pucode, patientName, errorDetails = {}, callState = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_ACCEPT_FAILED,
    message: `User tried to accept call from ${patientName || 'patient'} but failed`,
    metadata: {
      patientName,
      errorMessage: errorDetails.message || 'Unknown error',
      errorName: errorDetails.name || 'Error',
      errorStack: errorDetails.stack || null,
      callState: {
        startConsult: callState.startConsult,
        isCallActive: callState.isCallActive,
        consultOpen: callState.consultOpen
      },
      ...errorDetails.metadata
    },
  });
};

export const logConsultHoldFailed = async (pucode, patientName, errorDetails = {}, callState = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_HOLD_FAILED,
    message: `User tried to hold call of ${patientName || 'patient'} but failed`,
    metadata: {
      patientName,
      errorMessage: errorDetails.message || 'Unknown error',
      errorName: errorDetails.name || 'Error',
      errorStack: errorDetails.stack || null,
      callState: {
        startConsult: callState.startConsult,
        isCallActive: callState.isCallActive,
        consultOpen: callState.consultOpen
      },
      ...errorDetails.metadata
    },
  });
};

export const logConsultResumeFailed = async (pucode, patientName, errorDetails = {}, callState = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_RESUME_FAILED,
    message: `User tried to resume call of ${patientName || 'patient'} but failed`,
    metadata: {
      patientName,
      errorMessage: errorDetails.message || 'Unknown error',
      errorName: errorDetails.name || 'Error',
      errorStack: errorDetails.stack || null,
      callState: {
        startConsult: callState.startConsult,
        isCallActive: callState.isCallActive,
        consultOpen: callState.consultOpen
      },
      ...errorDetails.metadata
    },
  });
};

export const logConsultEndFailed = async (pucode, patientName, errorDetails = {}, callState = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_END_FAILED,
    message: `User tried to end call of ${patientName || 'patient'} but failed`,
    metadata: {
      patientName,
      errorMessage: errorDetails.message || 'Unknown error',
      errorName: errorDetails.name || 'Error',
      errorStack: errorDetails.stack || null,
      callState: {
        startConsult: callState.startConsult,
        isCallActive: callState.isCallActive,
        consultOpen: callState.consultOpen
      },
      ...errorDetails.metadata
    },
  });
};

export const logConsultDeclineFailed = async (pucode, patientName, errorDetails = {}, callState = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_DECLINE_FAILED,
    message: `User tried to decline call from ${patientName || 'patient'} but failed`,
    metadata: {
      patientName,
      errorMessage: errorDetails.message || 'Unknown error',
      errorName: errorDetails.name || 'Error',
      errorStack: errorDetails.stack || null,
      callState: {
        startConsult: callState.startConsult,
        isCallActive: callState.isCallActive,
        consultOpen: callState.consultOpen
      },
      ...errorDetails.metadata
    },
  });
};

export const logConsultSilenceFailed = async (pucode, patientName, errorDetails = {}, callState = {}) => {
  return logConsultationEvent({
    pucode,
    actor: ACTORS.PHARMACIST,
    event: CONSULTATION_EVENTS.CONSULT_SILENCE_FAILED,
    message: `User tried to silence call from ${patientName || 'patient'} but failed`,
    metadata: {
      patientName,
      errorMessage: errorDetails.message || 'Unknown error',
      errorName: errorDetails.name || 'Error',
      errorStack: errorDetails.stack || null,
      callState: {
        startConsult: callState.startConsult,
        isCallActive: callState.isCallActive,
        consultOpen: callState.consultOpen
      },
      ...errorDetails.metadata
    },
  });
};

/**
 * WebSocket Status Logging
 * Creates separate audit records for each WebSocket server that has connectivity issues
 */

export const logMainSocketDegradedOnCall = async (pucode, patientName, socketInfo = {}) => {
  const { status, error } = socketInfo;
  
  // Only log if not connected
  if (status === 'connected') {
    return; // Healthy, no need to audit
  }
  
  let statusMessage;
  if (status === 'error' && error) {
    statusMessage = `has an error: ${error}`;
  } else if (status === 'error') {
    statusMessage = 'has an error';
  } else if (error) {
    statusMessage = `is ${status} (${error})`;
  } else {
    statusMessage = `is ${status}`;
  }
  
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.MAIN_SOCKET_DEGRADED_ON_CALL,
    message: `Incoming call received but main consultation socket ${statusMessage}`,
    metadata: {
      patientName,
      socketType: 'main',
      socketPurpose: 'Consultation signaling (callAccepted, endSession, consultTimeout)',
      status: status,
      error: error
    }
  });
};

export const logPresenceSocketDegradedOnCall = async (pucode, patientName, socketInfo = {}) => {
  const { status, error } = socketInfo;
  
  // Only log if not connected
  if (status === 'connected') {
    return; // Healthy, no need to audit
  }
  
  let statusMessage;
  if (status === 'error' && error) {
    statusMessage = `has an error: ${error}`;
  } else if (status === 'error') {
    statusMessage = 'has an error';
  } else if (error) {
    statusMessage = `is ${status} (${error})`;
  } else {
    statusMessage = `is ${status}`;
  }
  
  return logConsultationEvent({
    pucode,
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.PRESENCE_SOCKET_DEGRADED_ON_CALL,
    message: `Incoming call received but presence tracking socket ${statusMessage}`,
    metadata: {
      patientName,
      socketType: 'presence',
      socketPurpose: 'Pharmacist presence tracking and heartbeats',
      status: status,
      error: error
    }
  });
};

export const logWebSocketStatusOnIncomingCall = async (pucode, patientName, socketStatus = {}) => {
  const { mainSocketStatus, mainSocketError, presenceSocketStatus, presenceSocketError } = socketStatus;
  
  const promises = [];
  
  // Log main socket issues
  if (mainSocketStatus !== 'connected') {
    promises.push(logMainSocketDegradedOnCall(pucode, patientName, {
      status: mainSocketStatus,
      error: mainSocketError
    }));
  }
  
  // Log presence socket issues
  if (presenceSocketStatus !== 'connected') {
    promises.push(logPresenceSocketDegradedOnCall(pucode, patientName, {
      status: presenceSocketStatus,
      error: presenceSocketError
    }));
  }
  
  // Execute all logging in parallel
  if (promises.length > 0) {
    await Promise.all(promises);
  }
};

/**
 * Initial WebSocket Connection Logging
 * Logs when WebSocket connections fail on initial attempt or recover after failure
 */

export const logMainSocketInitialConnectionFailed = async (error) => {
  return logConsultationEvent({
    pucode: 'SYSTEM',
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.MAIN_SOCKET_INITIAL_CONNECTION_FAILED,
    message: `Main consultation socket failed to connect initially${error ? `: ${error}` : ''}`,
    metadata: {
      socketType: 'main',
      socketPurpose: 'Consultation signaling (callAccepted, endSession, consultTimeout)',
      error: error || 'Unknown error'
    }
  });
};

export const logMainSocketConnectionRecovered = async () => {
  return logConsultationEvent({
    pucode: 'SYSTEM',
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.MAIN_SOCKET_CONNECTION_RECOVERED,
    message: 'Main consultation socket connection recovered after initial failure',
    metadata: {
      socketType: 'main',
      socketPurpose: 'Consultation signaling (callAccepted, endSession, consultTimeout)'
    }
  });
};

export const logPresenceSocketInitialConnectionFailed = async (error) => {
  return logConsultationEvent({
    pucode: 'SYSTEM',
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.PRESENCE_SOCKET_INITIAL_CONNECTION_FAILED,
    message: `Presence tracking socket failed to connect initially${error ? `: ${error}` : ''}`,
    metadata: {
      socketType: 'presence',
      socketPurpose: 'Pharmacist presence tracking and heartbeats',
      error: error || 'Unknown error'
    }
  });
};

export const logPresenceSocketConnectionRecovered = async () => {
  return logConsultationEvent({
    pucode: 'SYSTEM',
    actor: ACTORS.SYSTEM,
    event: CONSULTATION_EVENTS.PRESENCE_SOCKET_CONNECTION_RECOVERED,
    message: 'Presence tracking socket connection recovered after initial failure',
    metadata: {
      socketType: 'presence',
      socketPurpose: 'Pharmacist presence tracking and heartbeats'
    }
  });
};




// WEBPACK FOOTER //
// ./src/utils/consultationAuditLogger.js